import { forwardRef, useRef } from 'react';

import cn from 'classnames';

import styles from './ScrollableArea.module.css';
import type { ScrollableAreaProps } from './types';
import { useMousePointerScrollable } from './useMousePointerScrollable';

/**
 * ScrollableArea component with a constant scrollbar in different OS
 */
export const ScrollableArea = forwardRef<HTMLDivElement, ScrollableAreaProps>(
  (
    {
      children,
      direction = 'vertical',
      snapType,
      snapAlign = 'start',
      hideScrollBar = false,
      persistentScrollbar = false,
      dataTestId,
      role = 'region',
      onKeyDown,
      onScroll,
      className,
      isDraggable,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { onMouseDown, className: draggableClassName } =
      useMousePointerScrollable({
        isDraggable,
        direction,
        ref: containerRef,
      });

    const rootClassName = cn(
      styles['scrollable-area'],
      styles[`scrollable-area-snap-align-${snapAlign}`],
      styles[direction],
      snapType && styles[`snap-${snapType}`],
      {
        [styles['hide-scroll-bar']]: hideScrollBar,
        [styles['persistent-scrollbar']]: persistentScrollbar,
      },
      draggableClassName,
      className
    );

    return (
      <div
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={rootClassName}
        data-testid={dataTestId}
        role={role}
        onKeyDown={onKeyDown}
        onMouseDown={onMouseDown}
        onScroll={onScroll}
      >
        {children}
      </div>
    );
  }
);

ScrollableArea.displayName = 'ScrollableArea';
